const clean = (path: string) => path.replace(/^\//, '').replace('/$', '')

const gateway = (path: string) => {
  return (
    import.meta.env.REACT_APP_GATEWAY_BASE?.replace(/\/$/, '') +
    '/' +
    clean(path)
  )
}

const authorization = (path: string) => {
  return gateway(`/authorization/api/${clean(path)}`)
}

const translationOrder = (path: string) => {
  return gateway(`/translation-order/api/${clean(path)}`)
}

const auditLog = (path: string) => {
  return gateway(`/audit-log/api/${clean(path)}`)
}

const machineTranslation = (path: string) => {
  return gateway(`/machine-translation/api/${clean(path)}`)
}

const catTool = (path: string) => {
  return gateway(`/cat2/api/${clean(path)}`)
}

export const endpoints = {
  ROLES: authorization('roles'),
  INSTITUTIONS: authorization('institutions'),
  INSTITUTION_VACATIONS: authorization('institution-vacations'),
  INSTITUTION_USER_VACATIONS: authorization('institution-user-vacations'),
  PRIVILEGES: authorization('privileges'),
  USERS: authorization('institution-users'),
  VALIDATE_CSV: authorization('institution-users/validate-import-csv'),
  IMPORT_CSV: authorization('institution-users/import-csv'),
  DEPARTMENTS: authorization('departments'),
  DEPARTMENTS_BULK: authorization('departments/bulk'),
  EXPORT_CSV: authorization('institution-users/export-csv'),
  ARCHIVE_USER: authorization('institution-users/archive'),
  DEACTIVATE_USER: authorization('institution-users/deactivate'),
  ACTIVATE_USER: authorization('institution-users/activate'),
  PROJECT_TM: authorization(
    'institution-users/project-managers-assignable-by-client'
  ),
  PROJECT_CLIENT: authorization('institution-users/assignable-clients'),

  TRANSLATION_USERS: translationOrder('institution-users'),
  INSTITUTION_USER_VENDOR: (id: string) =>
    translationOrder(`institution-users/${id}/vendor`),
  TAGS: translationOrder('tags'),
  CREATE_TAGS: translationOrder('tags/bulk-create'),
  UPDATE_TAGS: translationOrder('tags/bulk-update'),
  VENDORS: translationOrder('vendors'),
  VENDORS_BULK: translationOrder('vendors/bulk'),
  VOLUMES: translationOrder('volumes'),
  SKILLS: translationOrder('skills'),
  PRICES: translationOrder('prices'),
  EDIT_PRICES: translationOrder('prices/bulk'),
  CLASSIFIER_VALUES: translationOrder('classifier-values'),
  PROJECTS: translationOrder('projects'),
  SUB_PROJECTS: translationOrder('subprojects'),
  TASKS: translationOrder('workflow/tasks'),
  TASKS2: translationOrder('workflow/tasks2'),
  HISTORY_TASKS: translationOrder('workflow/history/tasks'),
  HISTORY_TASKS2: translationOrder('workflow/history/tasks2'),
  ASSIGNMENTS: translationOrder('assignments'),
  STATISTICS: translationOrder('statistics'),
  DISCOUNTS: translationOrder('institution-discounts'),
  MEDIA: translationOrder('media'),
  MEDIA_BULK: translationOrder('media/bulk'),
  MEDIA_DOWNLOAD: translationOrder('media/download'),
  TM_SUB_PROJECTS: translationOrder('tm-keys/subprojects'),

  CALENDAR_LANGUAGES: translationOrder('calendar/languages'),
  INSTITUTION_MAIN_LANGUAGES: translationOrder('institutions/main-languages'),
  CALENDAR_DAY: translationOrder('calendar/day'),
  CALENDAR_WEEK: translationOrder('calendar/week'),
  CALENDAR_MONTH: translationOrder('calendar/month'),
  CALENDAR_SEARCH: translationOrder('calendar/search'),
  CALENDAR_SLOT_MATCHING: translationOrder('calendar/slot-matching/vendors'),
  PINNED_LANGUAGES: translationOrder('institution-users/pinned-languages'),
  VENDOR_CALENDAR: (vendorId: string) =>
    translationOrder(`vendors/${vendorId}/calendar`),
  VENDOR_EMERGENCY_SCHEDULES: (vendorId: string) =>
    translationOrder(`vendors/${vendorId}/emergency-schedules`),
  VENDOR_EMERGENCY_SCHEDULE: (vendorId: string, scheduleId: string) =>
    translationOrder(`vendors/${vendorId}/emergency-schedules/${scheduleId}`),
  PROJECT_CANCEL_DECLINE: (id: string) =>
    translationOrder(`projects/${id}/cancel-decline`),
  PROJECT_COMMENTS: (id: string) => translationOrder(`projects/${id}/comments`),
  PROJECT_COMMENT: (projectId: string, commentId: string) =>
    translationOrder(`projects/${projectId}/comments/${commentId}`),
  CALENDAR_PREBOOK: translationOrder('calendar/prebook'),
  CALENDAR_VENDOR_ENTRIES: translationOrder('calendar/vendor-entries'),
  CALENDAR_VENDOR_ENTRY: (entryId: string) =>
    translationOrder(`calendar/vendor-entries/${entryId}`),
  CALENDAR_IMPORT: translationOrder('calendar/import'),
  CALENDAR_IMPORT_BULK: translationOrder('calendar/import/bulk'),
  INSTITUTION_SETTINGS: translationOrder('institution/settings'),

  TRANSLATION_MEMORIES: catTool('translation-memories'),
  TRANSLATION_MEMORIES_IMPORT: catTool('translation-memories/import'),
  TRANSLATION_MEMORIES_EXPORT: catTool('translation-memories/export'),
  TM_CONTENT_CHECKS: catTool('translation-memories/context-checks'),

  AUDIT_LOGS: auditLog('event-records'),
  AUDIT_LOG_ACTIONS: auditLog('event-records/actions'),
  AUDIT_LOG_SETTING: (institution_id?: string) =>
    auditLog(`institutions/${institution_id}/settings`),
  EXPORT_AUDIT_LOGS: auditLog('event-records/export'),

  AUDIT_LOGS_OLD: auditLog('event-records-old'),
  EXPORT_AUDIT_LOGS_OLD: auditLog('event-records-old/export'),

  MT_PROVIDERS: machineTranslation('providers'),
  MT_PROVIDER_OPTIONS: (provider: string) =>
    machineTranslation(`providers/${provider}/options`),
  MT_TRANSLATE_TEXT: machineTranslation('translate/text'),
  MT_TRANSLATE_FILE: machineTranslation('translate/file'),
  MT_JOBS: machineTranslation('translate/jobs'),
  MT_FILE_DOWNLOAD: (id: string) =>
    machineTranslation(`translate/file/${id}/download`),
  MT_INSTITUTION_SETTINGS: machineTranslation('settings'),

  OUTSOURCE_REQUESTS: translationOrder('outsource-requests'),
  OUTSOURCE_REQUEST: (id: string) =>
    translationOrder(`outsource-requests/${id}`),
  OUTSOURCE_REQUEST_CANCEL: (id: string) =>
    translationOrder(`outsource-requests/${id}/cancel`),
  OUTSOURCE_REQUEST_SELECT: (id: string) =>
    translationOrder(`outsource-requests/${id}/select`),
  OUTSOURCE_REQUEST_ACCEPT: (id: string) =>
    translationOrder(`outsource-requests/${id}/accept`),
  OUTSOURCE_REQUEST_DECLINE: (id: string) =>
    translationOrder(`outsource-requests/${id}/decline`),
  OUTSOURCE_REQUEST_PREVIEW_PRICES: translationOrder(
    'outsource-requests/preview-prices'
  ),
  OUTSOURCE_OFFERS: translationOrder('outsource-offers'),
  OUTSOURCE_OFFER: (id: string) => translationOrder(`outsource-offers/${id}`),
  OUTSOURCE_OFFER_ACCEPT: (id: string) =>
    translationOrder(`outsource-offers/${id}/accept`),
  OUTSOURCE_OFFER_DECLINE: (id: string) =>
    translationOrder(`outsource-offers/${id}/decline`),
  INSTITUTION_PARTNERS: translationOrder('institution-partners'),
  INSTITUTION_PARTNERS_BULK: translationOrder('institution-partners/bulk'),
  INSTITUTION_PARTNER_PRICES: translationOrder('institution-partner-prices'),
  INSTITUTION_PARTNER_PRICES_BULK: translationOrder(
    'institution-partner-prices/bulk'
  ),
  TRANSLATION_ORDER_INSTITUTIONS: translationOrder('institutions'),
}

export const authEndpoints = {
  CONTEXT: gateway('/context'),
  SWITCH_CONTEXT: gateway('/switch-context'),
  LOGIN: gateway('/login'),
  LOGOUT: gateway('/logout'),
  SESSIONS: gateway('/sessions'),
  INVALIDATE_SESSION: (sessionId: string) => gateway(`/sessions/${sessionId}`),
}
